import React, { useState, useMemo } from 'react';
import OTPInput from 'react-otp-input';
import { CircleSpinner } from 'react-spinners-kit';
import { PaymentData, SimpliPayConfig, CardDetails, TransactionResult } from '../types';
import { generateAuthData, formatExpiryForApi, cleanPan } from '../utils/encryption';
import { createApiClient, initiatePayment } from '../utils/api';

interface PinEntryProps {
  paymentData: PaymentData;
  config: SimpliPayConfig;
  cardDetails: CardDetails;
  setStep: (step: any) => void;
  goBack: () => void;
  onClose: () => void;
  setError: (error: string) => void;
  setTransactionResponse: (response: any) => void;
  onSuccess: (result: TransactionResult) => void;
}

const PinEntry: React.FC<PinEntryProps> = ({
  paymentData,
  config,
  cardDetails,
  setStep,
  goBack,
  setError,
  setTransactionResponse,
}) => {
  const [pin, setPin] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    if (pin.length !== 4) {
      setError('Please enter a 4-digit PIN');
      return;
    }

    setIsLoading(true);

    try {
      const authData = await generateAuthData({
        version: '1',
        pan: cleanPan(cardDetails.pan),
        pin: pin,
        expiryDate: formatExpiryForApi(cardDetails.expiryDate),
        cvv: cardDetails.cvv,
      });

      const client = createApiClient(config);
      const response = await initiatePayment(client, {
        organizationId: paymentData.organizationId,
        transactionRef: paymentData.transactionRef,
        customerId: paymentData.customerId,
        customerEmail: paymentData.email,
        customerPhone: paymentData.phone,
        amount: paymentData.amount,
        currency: paymentData.currency,
        paymentMethod: 'CARD',
        serviceId: paymentData.serviceId,
        authData: authData,
        narration: paymentData.narration,
      });

      setTransactionResponse(response);
      setStep('otp');
    } catch (error: any) {
      setError(error?.response?.data?.message || 'Transaction failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="simplipay-step-container">
      <div className="simplipay-step-header">
        <button className="back-btn" onClick={goBack}>←</button>
        <h2>Transaction PIN</h2>
        <button className="close-btn" onClick={goBack}>×</button>
      </div>

      {isLoading ? (
        <div className="simplipay-loading">
          <CircleSpinner size={40} color="#46246b" />
          <p>Processing payment...</p>
        </div>
      ) : (
        <>
          <p className="simplipay-description">
            Please enter your 4-digit transaction PIN below.
          </p>

          <div className="simplipay-otp-container">
            <label>PIN</label>
            <OTPInput
              value={pin}
              onChange={setPin}
              numInputs={4}
              renderInput={(props) => <input {...props} type="password" inputMode="numeric" />}
              containerStyle="simplipay-otp-box"
              inputStyle="simplipay-otp-input"
              shouldAutoFocus
            />
          </div>

          <button
            className="simplipay-button"
            onClick={handleSubmit}
            disabled={pin.length !== 4}
          >
            Confirm
          </button>
        </>
      )}

      <div className="simplipay-footer">
        <span>🔒 Secured by <strong>SimpliPay</strong></span>
      </div>
    </div>
  );
};

export default PinEntry;
